import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct } from "../lib/api";
import DOMPurify from "dompurify";
import { mediaUrl } from "../lib/format";
import ProductImage from "../components/ProductImage";
import ProductCard from "../components/ProductCard";
import Blocks from "../components/Blocks";
import Seo from "../components/Seo";
import { useCart } from "../context/CartContext";
import { eur } from "../lib/format";
import { Minus, Plus, Truck, RotateCcw, ChevronRight, ShoppingCart, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null);
    setNotFound(false);
    setActive(0);
    setQty(1);
    getProduct(slug).then((prod) => { setP(prod); setQty(Math.max(1, Number(prod.min_qty) || 1)); }).catch(() => setNotFound(true));
  }, [slug]);
  const minQty = Math.max(1, Number(p?.min_qty) || 1);

  if (notFound) return <div className="max-w-7xl mx-auto px-4 py-20 text-center"><h1 className="font-heading text-2xl">Producto no encontrado</h1><Link to="/tienda" className="text-brand mt-4 inline-block">Volver a la tienda</Link></div>;
  if (!p) return <div className="max-w-7xl mx-auto px-4 py-20"><div className="h-96 bg-secondary animate-pulse rounded-sm" /></div>;

  const cat = p.categories && p.categories[0];
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    sku: p.reference,
    description: p.short_description || p.meta_description,
    image: p.images || [],
    brand: { "@type": "Brand", name: "FABRIPAN" },
    offers: {
      "@type": "Offer",
      price: p.price,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      seller: { "@type": "Organization", name: "FABRIPAN" },
    },
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <Seo title={p.seo_title || p.name} description={p.meta_description || p.short_description || `${p.name} — ${p.reference ? `Ref. ${p.reference}. ` : ""}Equipamiento profesional de panadería fabricado por FABRIPAN, directo de fábrica a su panadería.`} keywords={p.keywords || `${p.name.toLowerCase()}, ${p.reference || ""}, ${(p.categories || []).map((c) => c.name.toLowerCase()).join(", ")}`} image={p.images && p.images[0]} jsonLd={jsonLd} type="product" />

      <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-5" data-testid="breadcrumb">
        <Link to="/" className="hover:text-brand">Inicio</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to="/tienda" className="hover:text-brand">Tienda</Link>
        {cat && <><ChevronRight className="h-3 w-3" /><Link to={`/categoria/${cat.slug}`} className="hover:text-brand">{cat.name}</Link></>}
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground truncate">{p.name}</span>
      </nav>

      {p.banner && (
        <div className="aspect-[16/5] rounded-2xl overflow-hidden border border-border mb-6" data-testid="product-banner">
          <img src={mediaUrl(p.banner)} alt={p.name} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="aspect-square bg-white border border-border rounded-2xl overflow-hidden">
            <ProductImage src={p.images && p.images[active]} alt={p.name} className="w-full h-full object-contain p-6" />
          </div>
          {p.images && p.images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className={`w-20 h-20 shrink-0 border rounded-sm overflow-hidden bg-white ${active === i ? "border-brand" : "border-border"}`} data-testid={`thumb-${i}`}>
                  <ProductImage src={img} alt={`${p.name} ${i + 1}`} className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {p.reference && <span className="text-xs font-mono text-muted-foreground">REFERENCIA {p.reference}</span>}
          <h1 className="font-heading font-bold text-3xl tracking-tighter mt-1">{p.name}</h1>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-heading font-extrabold text-4xl text-brand-ink">{eur(p.price)}</span>
            <span className="text-xs text-muted-foreground">IVA no incluido</span>
          </div>
          <span className="inline-block mt-2 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-sm">Fabricación bajo pedido</span>

          {p.short_description && <p className="mt-4 text-muted-foreground leading-relaxed">{p.short_description}</p>}
          {minQty > 1 && (
            <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-sm px-3 py-2 text-sm" data-testid="min-qty-notice">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Este producto se vende con una <strong>cantidad mínima de {minQty} unidades</strong>. La cantidad se ajustará automáticamente.</span>
            </div>
          )}

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-border rounded-sm">
              <button onClick={() => setQty((q) => Math.max(minQty, q - 1))} className="p-3 hover:bg-secondary transition-colors" data-testid="qty-minus" aria-label="Menos"><Minus className="h-4 w-4" /></button>
              <input value={qty} onChange={(e) => setQty(parseInt(e.target.value) || minQty)} onBlur={() => setQty((q) => Math.max(minQty, q))} className="w-14 text-center font-medium border-x border-border py-2" data-testid="qty-input" />
              <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-secondary transition-colors" data-testid="qty-plus" aria-label="Más"><Plus className="h-4 w-4" /></button>
            </div>
            <button
              onClick={() => { const q = Math.max(minQty, qty); setQty(q); addItem(p, q); toast.success(`${q} × ${p.name} añadido al pedido`); }}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-6 py-3 rounded-full transition-colors"
              data-testid="add-to-order-btn"
            >
              <ShoppingCart className="h-5 w-5" /> Añadir al pedido
            </button>
          </div>

          <div className="mt-6 space-y-2 border-t border-border pt-6">
            <div className="flex items-center gap-2 text-sm"><Truck className="h-4 w-4 text-brand" /> Enviamos su pedido personalizado a su empresa.</div>
            <div className="flex items-center gap-2 text-sm"><RotateCcw className="h-4 w-4 text-brand" /> Si el producto no es el solicitado, devolución en 15 días.</div>
          </div>

          {p.description && (
            <div className="mt-6">
              <h2 className="font-heading font-semibold text-lg mb-2">Descripción</h2>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{p.description}</p>
            </div>
          )}
          {p.extended_description && (
            <div className="mt-4 prose prose-sm max-w-none text-muted-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.extended_description) }} />
          )}

          {p.content_blocks && p.content_blocks.length > 0 && (
            <div className="mt-6"><Blocks blocks={p.content_blocks} testid="product-blocks" /></div>
          )}

          {p.features && Object.keys(p.features).length > 0 && (
            <div className="mt-6">
              <h2 className="font-heading font-semibold text-lg mb-2">Características</h2>
              <table className="w-full text-sm border border-border rounded-sm overflow-hidden">
                <tbody>
                  {Object.entries(p.features).map(([k, v], i) => (
                    <tr key={k} className={i % 2 ? "bg-secondary/50" : "bg-white"}>
                      <td className="px-3 py-2 font-medium border-b border-border">{k}</td>
                      <td className="px-3 py-2 text-muted-foreground border-b border-border">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {p.faq && p.faq.length > 0 && (
            <div className="mt-6">
              <h2 className="font-heading font-semibold text-lg mb-2">Preguntas frecuentes</h2>
              <Accordion type="single" collapsible>
                {p.faq.map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`}>
                    <AccordionTrigger className="text-left text-sm">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
      </div>

      {p.related && p.related.length > 0 && (
        <div className="mt-16">
          <h2 className="font-heading font-bold text-2xl tracking-tighter mb-5">Productos relacionados</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {p.related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}
